export const onKeyDown = (callback: () => void) => (event: React.KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    callback();
  }
};

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const trimList = <T>(list: T[], length: number): T[] => list.slice(0, length);

export const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Get the current deployment environment.
 * Uses hostname detection since every hosted environment (development at
 * moments.dev.mento.co, staging, production) runs a production build.
 * 'development' means either a Vite dev server on a laptop or the hosted
 * development environment.
 *
 * For local development, you can override by setting localStorage:
 *   localStorage.setItem('mentoEnvOverride', 'staging')
 *
 * @returns 'development' | 'staging' | 'production'
 */
export function getEnvironment(): 'development' | 'staging' | 'production' {
  // Check for localStorage override (useful for local development testing)
  if (typeof window !== 'undefined') {
    const override = localStorage.getItem('mentoEnvOverride');
    if (override === 'staging' || override === 'production' || override === 'development') {
      return override;
    }
  }

  // Development mode check (Vite built-in)
  if (import.meta.env.DEV) {
    return 'development';
  }

  // In production builds, check hostname to tell the hosted environments apart
  if (typeof window !== 'undefined') {
    return environmentForHostname(window.location.hostname);
  }

  // Default to production for unknown hostnames in production builds
  return 'production';
}

/**
 * Classify a hosted (production-build) hostname.
 * - Development: moments.dev.mento.co, dev.mento.co (a `dev` label)
 * - Staging: moments.staging.mento.co, staging-bot.mento.co, etc.
 * - Production: everything else, including unknown hostnames
 */
export function environmentForHostname(hostname: string): 'development' | 'staging' | 'production' {
  if (hostname.includes('staging') || hostname.includes('stage')) {
    return 'staging';
  }
  if (hostname.split('.').includes('dev')) {
    return 'development';
  }
  return 'production';
}

/**
 * Check if we're running in staging environment.
 */
export function isStaging(): boolean {
  return getEnvironment() === 'staging';
}

/**
 * Check if we're running in production environment.
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * Check if we're running in development environment.
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}
